import { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { formatCurrency, getStorageJSON } from "../../utils/helpers";
import { db } from "../../firebase/firestore";
import { addDoc, collection, doc, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { getErrorMessage, logError, logInfo, logSuccess, logWarning } from "../../utils/errorHandler";

function getOrderCreatedAtMs(order) {
  const fromMs = Number(order?.createdAtMs || 0);
  if (fromMs > 0) return fromMs;
  const parsed = new Date(order?.createdAt || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function getPaymentMethod(order) {
  return String(order?.payment?.method || "").toLowerCase();
}

function getCancellationState(order, nowMs) {
  const paymentMethod = getPaymentMethod(order);
  const cancellableMethods = new Set(["cod", "upi"]);

  if (!cancellableMethods.has(paymentMethod)) {
    return { show: false, allowed: false, reason: "", minutesLeft: 0, label: "" };
  }

  const status = String(order?.status || "").toLowerCase();
  const blockedStatuses = new Set([
    "cancelled",
    "delivered",
    "out",
    "out_for_delivery",
    "on_the_way",
    "dispatch",
    "dispatched",
    "shipped",
  ]);

  const createdAtMs = getOrderCreatedAtMs(order);
  const elapsedMs = Math.max(0, nowMs - createdAtMs);
  const cancelWindowMs = paymentMethod === "upi" ? 15 * 60 * 1000 : 30 * 60 * 1000;
  const remainingMs = Math.max(0, cancelWindowMs - elapsedMs);
  const minutesLeft = Math.max(0, Math.ceil(remainingMs / 60000));
  const label = paymentMethod === "upi" ? "UPI" : "COD";

  if (blockedStatuses.has(status)) {
    return {
      show: true,
      allowed: false,
      reason: status === "cancelled" ? "Order already cancelled." : "Cancellation is disabled once order is in delivery flow.",
      minutesLeft,
      label,
    };
  }

  if (remainingMs <= 0) {
    return {
      show: true,
      allowed: false,
      reason: `Cancellation window ended after ${paymentMethod === "upi" ? "15" : "30"} minutes.`,
      minutesLeft: 0,
      label,
    };
  }

  return {
    show: true,
    allowed: true,
    reason: "",
    minutesLeft,
    label,
  };
}

function downloadInvoice(order) {
  const pdf = new jsPDF();
  const left = 12;
  const right = 198;
  const status = String(order.status || "pending").toUpperCase();

  pdf.setFillColor(249, 115, 22);
  pdf.rect(0, 0, 210, 30, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(19);
  pdf.text("Shreeji Surti Swad", left, 18);
  pdf.setFontSize(11);
  pdf.text("ORDER DOCUMENT", right, 18, { align: "right" });

  pdf.setTextColor(31, 31, 31);
  pdf.setDrawColor(232, 232, 232);
  pdf.roundedRect(left, 36, 186, 52, 3, 3, "S");

  pdf.setFontSize(10);
  pdf.setTextColor(80, 80, 80);
  pdf.text("Order Details", left + 4, 44);
  pdf.text("Customer", 116, 44);

  pdf.setFontSize(11);
  pdf.setTextColor(24, 24, 24);
  pdf.text(`Order ID: ${order.id}`, left + 4, 52);
  pdf.text(`Date: ${new Date(order.createdAt).toLocaleString()}`, left + 4, 58);
  pdf.text(`Area: ${order.location || "Malad"}`, left + 4, 64);
  pdf.text(`ETA: ${order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString() : "Not available"}`, left + 4, 70);
  pdf.text(`Payment: ${String(order.payment?.method || "upi").toUpperCase()}`, left + 4, 76);

  pdf.text(`Name: ${order.customer?.name || "User"}`, 116, 52);
  pdf.text(`Email: ${order.customer?.email || "N/A"}`, 116, 58);
  pdf.text(`Phone: ${order.customer?.phone || "N/A"}`, 116, 64);
  pdf.text(`Status: ${status}`, 116, 70);

  if (status === "CANCELLED") {
    pdf.setTextColor(220, 53, 69);
    pdf.setFontSize(28);
    pdf.text("CANCELLED", 105, 150, { align: "center", angle: 335 });
    pdf.setFontSize(10);
    pdf.text(`Cancelled At: ${order.cancelledAt ? new Date(order.cancelledAt).toLocaleString() : "N/A"}`, left + 4, 84);
    pdf.text(`Reason: ${order.cancelReason || "Customer cancellation"}`, left + 4, 89);
  }

  let y = status === "CANCELLED" ? 102 : 96;
  pdf.setFillColor(246, 246, 246);
  pdf.rect(left, y - 6, 186, 9, "F");
  pdf.setFontSize(10);
  pdf.setTextColor(70, 70, 70);
  pdf.text("Item", left + 2, y);
  pdf.text("Qty", 136, y);
  pdf.text("Rate", 158, y);
  pdf.text("Amount", right - 2, y, { align: "right" });
  y += 8;

  order.items?.forEach((item, index) => {
    const qty = Number(item.qty || 0);
    const rate = Number(item.price || 0);
    const price = rate * qty;
    pdf.setTextColor(22, 22, 22);
    pdf.text(`${index + 1}. ${item.name}`, left + 2, y);
    pdf.text(String(qty), 136, y);
    pdf.text(formatCurrency(rate), 158, y);
    pdf.text(formatCurrency(price), right - 2, y, { align: "right" });
    y += 7;
  });

  y += 4;
  pdf.line(left, y, right, y);
  y += 8;
  const subtotal = order.pricing?.subtotal ?? order.total ?? 0;
  const gst = order.pricing?.gst ?? 0;
  const delivery = order.pricing?.delivery ?? 0;
  const total = order.pricing?.total ?? order.total ?? subtotal + gst + delivery;

  pdf.setFontSize(11);
  pdf.setTextColor(40, 40, 40);
  pdf.text(`Subtotal: ${formatCurrency(subtotal)}`, 130, y);
  y += 7;
  pdf.text(`GST: ${formatCurrency(gst)}`, 130, y);
  y += 7;
  pdf.text(`Delivery: ${formatCurrency(delivery)}`, 130, y);
  y += 7;
  pdf.setFontSize(14);
  pdf.setTextColor(249, 115, 22);
  pdf.text(`Total: ${formatCurrency(total)}`, 130, y);

  y += 9;
  pdf.setTextColor(95, 95, 95);
  pdf.setFontSize(10);
  pdf.text("Thank you for ordering with us.", left, y);
  y += 5;
  pdf.text("Support: +91 8454874147 | shreejisurtiswad@gmail.com", left, y);

  pdf.save(`invoice_${order.id}.pdf`);
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [statusMessage, setStatusMessage] = useState("");
  const [ordersError, setOrdersError] = useState("");
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [nowMs, setNowMs] = useState(Date.now());
  const [viewInvoiceOrder, setViewInvoiceOrder] = useState(null);

  useEffect(() => {
    const user = getStorageJSON("user", null);

    if (!user?.uid) {
      const loadLocalOrders = () => {
        const data = getStorageJSON("orders", []);
        setOrders(Array.isArray(data) ? data : []);
        setLoadingOrders(false);
        setOrdersError("");
      };

      loadLocalOrders();
      window.addEventListener("storage", loadLocalOrders);
      return () => window.removeEventListener("storage", loadLocalOrders);
    }

    try {
      const q = query(collection(db, "orders"), where("userId", "==", user.uid));
      const unsubscribe = onSnapshot(
        q,
        (snap) => {
          try {
            const allOrders = snap.docs.map((d) => ({ firestoreId: d.id, ...d.data() }));
            const myOrders = allOrders.filter((o) => o.userId === user.uid);
            setOrders(myOrders);
            setLoadingOrders(false);
            setOrdersError("");
            logSuccess("Orders", "Orders loaded successfully");
          } catch (err) {
            logError("Orders Snapshot Parse", err);
            const data = getStorageJSON("orders", []);
            setOrders(Array.isArray(data) ? data : []);
            setLoadingOrders(false);
            setOrdersError(getErrorMessage(err));
          }
        },
        (err) => {
          logError("Orders Listener", err);
          const data = getStorageJSON("orders", []);
          setOrders(Array.isArray(data) ? data : []);
          setLoadingOrders(false);
          setOrdersError("Realtime order sync failed. Showing saved orders.");
        }
      );

      return () => unsubscribe();
    } catch (err) {
      logError("Orders Setup", err);
      const data = getStorageJSON("orders", []);
      setOrders(Array.isArray(data) ? data : []);
      setLoadingOrders(false);
      setOrdersError(getErrorMessage(err));
      return undefined;
    }
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 15000);
    return () => window.clearInterval(timer);
  }, []);

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const aTs = Number(a.createdAtMs || 0) || new Date(a.createdAt || 0).getTime();
      const bTs = Number(b.createdAtMs || 0) || new Date(b.createdAt || 0).getTime();
      return bTs - aTs;
    });
  }, [orders]);

  const cancelOrder = async (order) => {
    setStatusMessage("");
    const user = getStorageJSON("user", null);
    const orderId = order.id || order.firestoreId;
    const cancelState = getCancellationState(order, nowMs);

    if (!orderId) {
      logWarning("Order Cancel", "Missing order id");
      setStatusMessage("Unable to cancel this order.");
      return;
    }

    if (!cancelState.allowed) {
      logWarning("Order Cancel", `Cancellation blocked for order ${orderId}`);
      setStatusMessage(cancelState.reason || `${cancelState.label || "Order"} cancellation is not available for this order.`);
      return;
    }

    const paymentMethod = getPaymentMethod(order);
    const windowMinutes = paymentMethod === "upi" ? 15 : 30;

    const nextPatch = {
      status: "cancelled",
      cancelledBy: "customer",
      cancelledAt: new Date().toISOString(),
      cancelledAtMs: Date.now(),
      cancelReason: `Cancelled by customer within ${windowMinutes} minutes (${String(paymentMethod || "order").toUpperCase()})`,
    };

    try {
      if (user?.uid && order.firestoreId) {
        logInfo("Order Cancel", `Updating Firestore order ${order.firestoreId}`);
        await updateDoc(doc(db, "orders", order.firestoreId), nextPatch);
      }
    } catch (err) {
      logError("Order Cancel Firestore", err);
      const errorMsg = getErrorMessage(err);
      setStatusMessage(`Unable to cancel now. ${errorMsg || `${windowMinutes}-minute window ended or server rule blocked this request.`}`);
      return;
    }

    try {
      if (user?.uid) {
        await addDoc(collection(db, "notifications"), {
          userId: user.uid,
          title: "Order cancelled",
          message: `Order ${orderId} cancelled successfully.`,
          createdAt: new Date().toISOString(),
          createdAtMs: Date.now(),
          read: false,
          orderId,
        });
      }
    } catch (err) {
      logError("Order Cancel Notification", err);
      // Non-blocking: order is already cancelled.
    }

    try {
      const localOrders = getStorageJSON("orders", []);
      const nextLocalOrders = Array.isArray(localOrders)
        ? localOrders.map((entry) => (entry.id === orderId ? { ...entry, ...nextPatch } : entry))
        : [];
      localStorage.setItem("orders", JSON.stringify(nextLocalOrders));

      const notifications = getStorageJSON("customerNotifications", []);
      const nextNotifications = [
        {
          id: `N-${Date.now()}`,
          title: "Order cancelled",
          message: `Order ${orderId} cancelled successfully.`,
          createdAt: new Date().toISOString(),
          createdAtMs: Date.now(),
          read: false,
          userId: user?.uid || "",
          orderId,
        },
        ...(Array.isArray(notifications) ? notifications : []),
      ];
      localStorage.setItem("customerNotifications", JSON.stringify(nextNotifications));
      window.dispatchEvent(new Event("notifications-updated"));

      setOrders((prev) => prev.map((entry) => ((entry.id || entry.firestoreId) === orderId ? { ...entry, ...nextPatch } : entry)));
      logSuccess("Order Cancel", `Order ${orderId} cancelled successfully`);
      setStatusMessage(`${cancelState.label || "Order"} cancelled successfully.`);
    } catch (err) {
      logError("Order Cancel Local Sync", err);
      setStatusMessage(`${cancelState.label || "Order"} cancelled, but local sync had an issue. Refresh page.`);
    }
  };

  return (
    <div className="home">
      <Navbar />

      <div className="customer-page">
        <div className="customer-page-header">
          <h2>My Orders</h2>
          <p>Track all your placed orders.</p>
        </div>

        {statusMessage && <p className="form-status">{statusMessage}</p>}

        {ordersError ? <div className="error-banner">⚠️ {ordersError}</div> : null}

        {loadingOrders ? (
          <p>Loading orders...</p>
        ) : !sortedOrders.length ? (
          <div className="empty-state">
            <h3>No orders yet</h3>
            <p>Your placed orders will appear here.</p>
          </div>
        ) : (
          <div className="customer-grid">
            {sortedOrders.map((order) => (
              <article className="customer-card" key={order.id || order.firestoreId}>
                {(() => {
                  const cancelState = getCancellationState(order, nowMs);
                  return (

                    <>
                <div className="row-between">
                  <h3>{order.id || order.firestoreId}</h3>
                  <span className="status-chip">{order.status || "Placed"}</span>
                </div>
                <div className="order-meta-block">
                  <p>{new Date(getOrderCreatedAtMs(order) || Date.now()).toLocaleString()}</p>
                  <p>Items: {order.items?.length || 0}</p>
                  <p>Delivery: {order.location || "Malad"}</p>
                  <p>Payment: {String(order.payment?.method || "UPI").toUpperCase()}</p>
                  {String(order.status || "").toLowerCase() === "cancelled" && (
                    <p className="cancel-hint">Cancelled order. Invoice includes cancellation details.</p>
                  )}
                </div>
                <strong>{formatCurrency(order.pricing?.total ?? order.total)}</strong>
                <div className="page-actions orders-actions">
                  <button className="secondary-action" onClick={() => setViewInvoiceOrder(order)}>
                    View Invoice
                  </button>
                  <button className="secondary-action" onClick={() => downloadInvoice(order)}>
                    Download Invoice
                  </button>
                  {cancelState.show && (
                    <button className="secondary-action" disabled={!cancelState.allowed} onClick={() => cancelOrder(order)}>
                      {cancelState.allowed ? `Cancel ${cancelState.label} (${cancelState.minutesLeft} min left)` : `Cancel ${cancelState.label} Unavailable`}
                    </button>
                  )}
                </div>
                {cancelState.show && !cancelState.allowed && <small className="cancel-hint">{cancelState.reason}</small>}
                    </>
                  );
                })()}
              </article>
            ))}
          </div>
        )}
      </div>

      {viewInvoiceOrder && (
        <div className="invoice-modal-overlay" onClick={() => setViewInvoiceOrder(null)}>
          <div className="invoice-modal" onClick={(event) => event.stopPropagation()}>
            <div className="row-between">
              <h3>Invoice {viewInvoiceOrder.id || viewInvoiceOrder.firestoreId}</h3>
              <button className="secondary-action" onClick={() => setViewInvoiceOrder(null)}>Close</button>
            </div>

            <div className="invoice-meta-grid">
              <p><b>Status:</b> {String(viewInvoiceOrder.status || "pending").toUpperCase()}</p>
              <p><b>Payment:</b> {String(viewInvoiceOrder.payment?.method || "upi").toUpperCase()}</p>
              <p><b>Date:</b> {new Date(getOrderCreatedAtMs(viewInvoiceOrder) || Date.now()).toLocaleString()}</p>
              <p><b>Delivery Area:</b> {viewInvoiceOrder.location || "Malad"}</p>
            </div>

            <div className="invoice-items-list">
              {(viewInvoiceOrder.items || []).map((item, index) => {
                const qty = Number(item.qty || 0);
                const price = Number(item.price || 0);
                return (
                  <div className="invoice-item-row" key={`${item.name || "item"}-${index}`}>
                    <span>{index + 1}. {item.name || "Item"}</span>
                    <span>{qty} x {formatCurrency(price)}</span>
                  </div>
                );
              })}
            </div>

            <div className="invoice-total-box">
              <p>Subtotal: {formatCurrency(viewInvoiceOrder.pricing?.subtotal ?? viewInvoiceOrder.total ?? 0)}</p>
              <p>GST: {formatCurrency(viewInvoiceOrder.pricing?.gst ?? 0)}</p>
              <p>Delivery: {formatCurrency(viewInvoiceOrder.pricing?.delivery ?? 0)}</p>
              <h4>Total: {formatCurrency(viewInvoiceOrder.pricing?.total ?? viewInvoiceOrder.total ?? 0)}</h4>
            </div>

            <div className="page-actions orders-actions">
              <button className="secondary-action" onClick={() => downloadInvoice(viewInvoiceOrder)}>Download Invoice</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
